import { Button, Grid, MenuItem, Paper, TextField, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import * as React from 'react';
import { makeStyles } from 'tss-react/mui';
import { useCreateIntegration } from '../../../sic-lib/api/hooks/useIntegrations';
import { IntegrationApiResponse } from '../../../sic-lib/api/integrations/types/IntegrationApiResponse';
import { ApiIntegrationType } from '../../../sic-lib/enums/ApiIntegrationType';
import { splitCamelCase } from '../../../sic-lib/utils/aiHelpers';
import AxiosErrorAlert from '../../Shared/Alerts/AxiosErrorAlert';
import ButtonLoading from '../../Shared/ButtonLoading';

interface Props {
    integrationData: IntegrationApiResponse[] | undefined;
    onCloseHandler: () => void;
}

export default function CreateIntegrations({ integrationData, onCloseHandler }: Props) {
    const { classes } = useStyles();

    const [integrations, setIntegrations] = React.useState<ApiIntegrationType[]>([]);
    const [selectedIntegration, setSelectedIntegration] = React.useState<
        ApiIntegrationType | undefined
    >(undefined);

    const {
        mutate: createIntegrationMutation,
        isPending: isCreateLoading,
        isSuccess: isCreateSuccess,
        isError: isCreateError,
        error: createError,
    } = useCreateIntegration();

    React.useEffect(() => {
        if (integrationData) {
            const enumValues = Object.values(ApiIntegrationType).filter(
                value => typeof value === 'number',
            ) as ApiIntegrationType[];
            const dataValues = integrationData.map(itm => itm.sourceId);
            const missingValues = enumValues.filter(value => !dataValues.includes(value));
            console.log('createIntegration: ', missingValues);
            setIntegrations(missingValues);
        }
    }, [integrationData]);

    const handleSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedIntegration(+event.target.value);
    };

    const handleSubmit = () => {
        if (selectedIntegration) {
            createIntegrationMutation(selectedIntegration);
            setTimeout(() => {
                onCloseHandler();
            }, 6000);
        }
    };

    if (integrations.length === 0) {
        return (
            <Grid mb={2} flexDirection="column" width="100%" alignItems="flex-end">
                <AxiosErrorAlert
                    error="No integration available at this moment"
                    severity="info"
                    className={classes.list}
                />
                <Grid item mt={1} xs="auto" display="flex" justifyContent="flex-end">
                    <Button onClick={onCloseHandler}>Cancel</Button>
                </Grid>
            </Grid>
        );
    }

    return (
        <Paper className={classes.marginBot}>
            <Grid className={classes.header}>
                <Typography component="h3" variant="subtitle1">
                    Create Integrations
                </Typography>
            </Grid>
            <Grid
                container
                className={classes.list}
                direction="row"
                justifyContent="space-between"
                alignItems="center"
            >
                <Typography component="p" variant="body2">
                    Select Integration:
                </Typography>
                <TextField
                    className={classes.selectWrapper}
                    select
                    value={selectedIntegration}
                    id="select_integration"
                    variant="outlined"
                    label="Select Integration"
                    fullWidth
                    onChange={handleSelect}
                >
                    {integrations.map(itm => (
                        <MenuItem key={itm} value={itm} className={classes.menuItem}>
                            {splitCamelCase(ApiIntegrationType[itm])}
                        </MenuItem>
                    ))}
                </TextField>
            </Grid>
            <Grid
                container
                direction="row-reverse"
                wrap="nowrap"
                spacing={2}
                className={classes.list}
            >
                <Grid item xs="auto">
                    <ButtonLoading
                        isLoading={isCreateLoading}
                        isSuccess={isCreateSuccess}
                        successLabel="Successfully Created"
                        onClick={handleSubmit}
                        color="secondary"
                        variant="outlined"
                    >
                        Save
                    </ButtonLoading>
                </Grid>
                <Grid item xs="auto">
                    <Button onClick={onCloseHandler}>Cancel</Button>
                </Grid>
            </Grid>
            {isCreateError && <AxiosErrorAlert error={createError} className={classes.list} />}
        </Paper>
    );
}

const useStyles = makeStyles()((theme: Theme) => ({
    root: {
        margin: theme.spacing(2),
    },
    header: {
        position: 'relative',
        padding: theme.spacing(2),
        '& > h3': {
            textTransform: 'capitalize',
        },
        '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            display: 'block',
            width: '100%',
            borderBottom: `1px solid ${theme.palette.grey[300]}`,
        },
    },
    list: {
        padding: theme.spacing(2),
    },
    marginBot: {
        marginBottom: theme.spacing(2),
    },
    selectWrapper: {
        width: '25%',
    },
    menuItem: {
        '&:hover': {
            backgroundColor: theme.palette.menuListHover.light,
        },
        '&.Mui-selected': {
            backgroundColor: theme.palette.menuListHover.main,
            '&:hover': {
                backgroundColor: theme.palette.menuListHover.main,
            },
        },
    },
}));
